import { Common } from '../common';
import {
  CreateValues,
  PracticePreference,
  Preferences,
  PreferenceOptions,
  UpdateValues,
  PracticePreferenceHistory,
} from '../models/practice/preference';
import { RequireExactlyOne } from 'type-fest';

interface PreferenceResponse {
  preferences?: PracticePreference;
  preferenceOptions: PreferenceOptions;
}

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findAllByOrganizationId(organizationId: string): Promise<{
    practicePreferences: PracticePreference[];
    practiceGroupPreferences: PracticePreference[];
  }> {
    const url = this.common.constructPath('/practice-preferences/all', {
      organizationId,
    });
    return this.common.getJSON(url);
  }

  async findAllByPracticeGroupId(practiceGroupId: string): Promise<{
    practicePreferences: PracticePreference[];
  }> {
    const url = this.common.constructPath('/practice-preferences/all', {
      practiceGroupId,
    });
    return this.common.getJSON(url);
  }

  async findOneByPracticeId(practiceId: string): Promise<PreferenceResponse> {
    const url = this.common.constructPath(`/practice-preferences`, {
      practiceId,
    });
    return this.common.getJSON(url);
  }

  async findOneByPracticeGroupId(
    practiceGroupId: string,
  ): Promise<PreferenceResponse> {
    const url = this.common.constructPath(`/practice-preferences`, {
      practiceGroupId,
    });
    return this.common.getJSON(url);
  }

  async findOneByOrganizationId(
    organizationId: string,
  ): Promise<PreferenceResponse> {
    const url = this.common.constructPath(`/practice-preferences`, {
      organizationId,
    });
    return this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<PracticePreference> {
    const url = this.common.constructPath(`/practice-preferences/${id}`);
    return this.common.getJSON(url);
  }

  async findHistory(
    practicePreferenceId: string,
  ): Promise<PracticePreferenceHistory[]> {
    const url = this.common.constructPath(
      `/practice-preferences/${practicePreferenceId}/history`,
    );
    return this.common.getJSON(url);
  }

  async create(values: CreateValues): Promise<PracticePreference> {
    const url = this.common.constructPath(`/practice-preferences`);
    return this.common.postJSON(url, values);
  }

  async update(id: string, values: UpdateValues): Promise<PracticePreference> {
    const url = this.common.constructPath(`/practice-preferences/${id}`);
    return this.common.patchJSON(url, values);
  }

  async remove(id: string): Promise<{ success: true }> {
    const url = this.common.constructPath(`/practice-preferences/${id}`);
    return this.common.deleteJSON(url);
  }

  async override(values: {
    attribute: string;
    preferenceIds: string[];
  }): Promise<{ success: true }> {
    const url = this.common.constructPath(`/practice-preferences/override`);
    return this.common.patchJSON(url, values);
  }

  async getInherited(
    args: RequireExactlyOne<{
      practiceId: string;
      practiceGroupId: string;
      organizationId: string;
    }>,
  ): Promise<Preferences> {
    let basePath;
    if (args.practiceId) {
      basePath = `/practices/${args.practiceId}`;
    } else if (args.practiceGroupId) {
      basePath = `/practice-groups/${args.practiceGroupId}`;
    } else if (args.organizationId) {
      basePath = `/organizations/${args.organizationId}`;
    } else {
      throw new Error('Unknown preference entity');
    }
    const url = this.common.constructPath(`${basePath}/preferences`);
    return this.common.getJSON(url);
  }
}
